import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, ease as mmEase } from "@/lib/motion";
import { ArrowLeft, Printer, Share2, CheckCircle2, Clock, XCircle, ArrowRight, WifiOff } from "lucide-react";
import Logo from "@/components/Logo";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CURRENCY_BY_CODE } from "@/lib/constants";
import { formatAmount, formatDate } from "@/lib/format";

const ez = mmEase.expo;

const STATUS = {
  completed:  { icon: CheckCircle2, color: "var(--color-success)", bg: "bg-emerald/[0.15]", label: "Completed"  },
  processing: { icon: Clock,        color: "var(--color-warning)", bg: "bg-gold/10",        label: "Processing" },
  pending:    { icon: Clock,        color: "var(--color-warning)", bg: "bg-gold/10",        label: "Pending"    },
  failed:     { icon: XCircle,      color: "var(--color-danger)",  bg: "bg-danger/10",      label: "Failed"     },
};

export default function TransactionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tx,         setTx]         = useState(null);
  const [fetchError, setFetchError] = useState(false);

  useEffect(() => {
    setFetchError(false);
    api.get(`/transactions/${id}`)
      .then(({ data }) => setTx(data))
      .catch(() => {
        setFetchError(true);
        toast.error("Could not load this transaction. Check your connection.");
      });
  }, [id]);

  if (!tx) return (
    <div className="max-w-2xl mx-auto space-y-5">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-1.5 text-sm font-bold text-muted-foreground hover:text-navy transition-colors"
      >
        <ArrowLeft size={14} /> Back to transactions
      </button>
      {fetchError ? (
        <div
          role="alert"
          data-testid="tx-detail-error"
          className="card-elevated p-8 text-center"
        >
          <div className="w-14 h-14 rounded-2xl bg-[#EF4444]/[0.08] grid place-items-center mx-auto mb-4" style={{ color: "#EF4444" }}>
            <WifiOff size={22} strokeWidth={1.6} />
          </div>
          <p className="font-bold text-navy text-sm">Could not load this transaction</p>
          <p className="text-xs text-muted-foreground mt-1 mb-4">Check your connection, then try again.</p>
          <button
            onClick={() => { setFetchError(false); api.get(`/transactions/${id}`).then(({ data }) => setTx(data)).catch(() => { setFetchError(true); toast.error("Still unable to load. Try refreshing the page."); }); }}
            className="btn-navy"
            data-testid="tx-detail-retry"
          >
            Retry
          </button>
        </div>
      ) : (
        <div className="card-elevated overflow-hidden">
          <div className="skeleton h-52 w-full" />
          <div className="p-6 space-y-4">
            <div className="skeleton h-32 rounded-2xl" />
            <div className="flex gap-3 justify-center">
              <div className="skeleton h-10 w-32 rounded-full" />
              <div className="skeleton h-10 w-32 rounded-full" />
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const fromC = CURRENCY_BY_CODE[tx.from_currency];
  const toC   = CURRENCY_BY_CODE[tx.to_currency];
  const s     = STATUS[tx.status] || STATUS.pending;
  const StatusIcon = s.icon;

  return (
    <div className="max-w-2xl mx-auto space-y-5">

      {/* Back */}
      <motion.button
        data-testid="tx-back"
        onClick={() => navigate(-1)}
        className="flex items-center gap-1.5 text-sm font-bold text-muted-foreground hover:text-navy transition-colors"
        initial={{ opacity: 0, x: -8 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.3, ease: ez }}
      >
        <ArrowLeft size={14} /> Back to transactions
      </motion.button>

      {/* ── Receipt card ─────────────────────────────────────────── */}
      <motion.div
        id="receipt"
        className="card-elevated overflow-hidden print:shadow-none"
        initial={{ opacity: 0, y: 16, filter: "blur(4px)" }}
        animate={{ opacity: 1, y: 0,  filter: "blur(0px)" }}
        transition={{ duration: 0.48, ease: ez, delay: 0.06 }}
      >
        {/* Header stripe */}
        <div className="brand-gradient px-7 pt-7 pb-8 relative overflow-hidden">
          <div
            className="absolute -top-16 -right-16 w-48 h-48 rounded-full blur-3xl pointer-events-none"
            style={{ background: "rgba(16,185,129,0.20)" }}
          />
          <div
            className="absolute -bottom-12 -left-8 w-36 h-36 rounded-full blur-3xl pointer-events-none"
            style={{ background: "rgba(6,182,212,0.12)" }}
          />
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />

          {/* Logo + reference */}
          <div className="relative flex items-start justify-between mb-7">
            <Logo variant="dark" size="md" />
            <div className="text-right">
              <p className="text-[9px] uppercase tracking-[0.2em] text-white/40 font-bold">Receipt</p>
              <p className="font-mono text-sm font-bold text-white/80 mt-0.5">{tx.reference}</p>
            </div>
          </div>

          {/* Status + amounts */}
          <div className="relative text-center">
            <motion.div
              className={`w-14 h-14 rounded-2xl ${s.bg} grid place-items-center mx-auto mb-3`}
              initial={{ scale: 0.7, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.45, ease: ez, delay: 0.18 }}
            >
              <StatusIcon size={26} style={{ color: s.color }} strokeWidth={2} />
            </motion.div>
            <p
              className="text-[10px] uppercase tracking-[0.2em] font-bold"
              style={{ color: s.color }}
            >
              {s.label}
            </p>
            <p className="font-display text-[2.1rem] font-black text-white tracking-tight mt-2 num-tabular">
              {formatAmount(tx.send_amount, tx.from_currency)}
              <span className="text-white/30 mx-2 text-2xl"><ArrowRight size={22} className="inline -mt-1" /></span>
              {formatAmount(tx.receive_amount, tx.to_currency)}
            </p>
            <p className="text-white/40 text-sm mt-1.5">
              {fromC?.flag} {tx.from_currency} → {tx.to_currency} {toC?.flag} · Rate {tx.rate}
            </p>
          </div>
        </div>

        {/* Detail grid */}
        <div className="p-6">
          <div className="grid sm:grid-cols-2 gap-4 bg-secondary/50 rounded-2xl p-5">
            <DetailField label="Date initiated"  value={formatDate(tx.created_at)} />
            <DetailField label="Date completed"  value={formatDate(tx.completed_at)} />
            <DetailField label="Delivery method" value={tx.method?.replace(/_/g, " ")} />
            <DetailField label="Fee paid"        value={formatAmount(tx.fee, tx.from_currency)} />

            {tx.beneficiary_snapshot && (
              <>
                <DetailField label="Recipient" value={tx.beneficiary_snapshot.name} />
                <DetailField label="Country"   value={tx.beneficiary_snapshot.country} />
                {tx.beneficiary_snapshot.bank_name      && <DetailField label="Bank"    value={tx.beneficiary_snapshot.bank_name} />}
                {tx.beneficiary_snapshot.account_number && <DetailField label="Account" value={tx.beneficiary_snapshot.account_number} mono />}
                {tx.beneficiary_snapshot.mobile_number  && <DetailField label="Mobile"  value={tx.beneficiary_snapshot.mobile_number} mono />}
                {tx.beneficiary_snapshot.wallet_email   && <DetailField label="Wallet"  value={tx.beneficiary_snapshot.wallet_email} />}
              </>
            )}
            {tx.note && <DetailField label="Note" value={tx.note} full />}
          </div>

          {/* Actions */}
          <div className="flex flex-wrap justify-center gap-3 mt-5 print:hidden">
            <button
              data-testid="tx-print"
              onClick={() => window.print()}
              className="btn-navy"
            >
              <Printer size={13} /> Print receipt
            </button>
            <button
              data-testid="tx-share"
              onClick={() => {
                navigator.clipboard.writeText(
                  `MoneyMeet receipt ${tx.reference} — ${formatAmount(tx.send_amount, tx.from_currency)} → ${formatAmount(tx.receive_amount, tx.to_currency)}`
                );
              }}
              className="btn-ghost-dark"
            >
              <Share2 size={13} /> Copy summary
            </button>
          </div>
        </div>

        <div className="px-7 pb-5 text-center text-[11px] text-muted-foreground">
          MoneyMeet Pay · Global Payments. One Wallet.
        </div>
      </motion.div>
    </div>
  );
}

function DetailField({ label, value, mono, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <p className="text-[9px] uppercase tracking-[0.18em] text-muted-foreground font-bold mb-0.5">{label}</p>
      <p className={`font-semibold text-navy text-sm ${mono ? "font-mono" : ""}`}>{value || "—"}</p>
    </div>
  );
}
